package com.merkost.handpass.admin.components

import androidx.compose.runtime.*
import com.merkost.handpass.admin.container.CommandPaletteContainer
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.sharedweb.theme.*
import com.merkost.handpass.sharedweb.theme.transitionColors
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.merkost.handpass.admin.di.koinContainer
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventListener
import pro.respawn.flowmvi.compose.dsl.subscribe

val CommandPaletteResultRowStyle = CssStyle {
    base {
        Modifier
            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
            .borderRadius(RadiusControl.value())
            .transitionColors()
    }
    hover {
        Modifier.backgroundColor(SurfaceMuted.value())
    }
}

private class PaletteKeyState {
    var isOpen: Boolean = false
    var resultCount: Int = 0
}

@Composable
fun AdminCommandPalette() {
    val container = koinContainer<CommandPaletteContainer>()
    val ctx = rememberPageContext()
    val keyState = remember { PaletteKeyState() }

    DisposableEffect(Unit) {
        val handler = EventListener { e: Event ->
            val key = (e.asDynamic().key as? String) ?: ""
            val isMeta = (e.asDynamic().metaKey as? Boolean) ?: false
            val isCtrl = (e.asDynamic().ctrlKey as? Boolean) ?: false
            if (key == "k" && (isMeta || isCtrl)) {
                e.asDynamic().preventDefault()
                container.store.intent(CommandPaletteIntent.Toggle)
                return@EventListener
            }
            if (!keyState.isOpen) return@EventListener
            when (key) {
                "ArrowDown" -> {
                    e.asDynamic().preventDefault()
                    container.store.intent(CommandPaletteIntent.MoveSelection(1))
                }
                "ArrowUp" -> {
                    e.asDynamic().preventDefault()
                    container.store.intent(CommandPaletteIntent.MoveSelection(-1))
                }
                "Enter" -> {
                    if (keyState.resultCount > 0) {
                        e.asDynamic().preventDefault()
                        container.store.intent(CommandPaletteIntent.ConfirmSelection)
                    }
                }
            }
        }
        window.addEventListener("keydown", handler)
        onDispose { window.removeEventListener("keydown", handler) }
    }

    with(container.store) {
        val state by subscribe { action ->
            when (action) {
                is CommandPaletteAction.Navigate -> ctx.router.navigateTo(action.path)
            }
        }
        keyState.isOpen = state.isOpen
        keyState.resultCount = (state.results as? AsyncSection.Loaded)?.value?.size ?: 0
        if (!state.isOpen) return

        AdminModal(onDismiss = { intent(CommandPaletteIntent.Close) }) {
            Div({ style { property("min-width", "480px"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(HandpassSpacing.MD) } }) {
                AdminInput(
                    value = state.query,
                    onChange = { intent(CommandPaletteIntent.SetQuery(it)) },
                    placeholder = "Search users, listings, or jump to a page…",
                )
                when (val data = state.results) {
                    AsyncSection.Loading -> Div(
                        attrs = Modifier.color(OnSurfaceMuted.value()).toAttrs()
                    ) { Text("Searching…") }
                    is AsyncSection.Failed -> Div(
                        attrs = Modifier.color(Danger.value()).toAttrs()
                    ) { Text(data.message) }
                    is AsyncSection.Loaded -> {
                        if (data.value.isEmpty()) Div(
                            attrs = Modifier.color(OnSurfaceMuted.value()).toAttrs()
                        ) { Text("No results") }
                        else data.value.forEachIndexed { index, result ->
                            val isSelected = index == state.selectedIndex
                            Div(
                                attrs = CommandPaletteResultRowStyle.toModifier()
                                    .then(if (isSelected) Modifier.backgroundColor(AccentSoft.value()) else Modifier)
                                    .styleModifier { property("padding", "${HandpassSpacing.MD} ${HandpassSpacing.Default}") }
                                    .toAttrs {
                                        attr("role", "option")
                                        attr("aria-selected", isSelected.toString())
                                        attr("tabindex", "0")
                                        onClick { intent(CommandPaletteIntent.TapResult(result)) }
                                        onKeyDown { event ->
                                            if (event.key == "Enter" || event.key == " ") {
                                                event.preventDefault()
                                                intent(CommandPaletteIntent.TapResult(result))
                                            }
                                        }
                                    }
                            ) {
                                AdminBadge(label = result.type.name.lowercase(), variant = AdminBadgeVariant.Neutral)
                                Span(
                                    attrs = Modifier
                                        .color(OnSurface.value())
                                        .styleModifier {
                                            fontSize(HandpassTypography.Body)
                                            property("font-weight", "500")
                                            property("margin-left", HandpassSpacing.SM.toString())
                                        }
                                        .toAttrs()
                                ) { Text(result.title) }
                                if (result.subtitle != null) {
                                    Span(
                                        attrs = Modifier
                                            .color(OnSurfaceMuted.value())
                                            .styleModifier {
                                                fontSize(HandpassTypography.Caption)
                                                property("margin-left", HandpassSpacing.SM.toString())
                                            }
                                            .toAttrs()
                                    ) { Text(result.subtitle) }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
